import { FinancialProductStrategy } from '@generated/graphql';

import type { TranslatedString } from '@interfaces/shared';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import TranslatableRichTextEditor from '@ovComponents/3-pattern/translatableRichTextEditor/translatableRichTextEditor';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { Box, Grid, Typography, useTheme } from 'hephaestus/web';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { AssetClassTargetAllocation } from '../addModelPortfoliosIPSAndAssetClasses/assetClassTargetAllocation.types';
import {
  hasUnusableAllocation,
  toEditableAllocations,
  toStoredAllocations,
} from '../addModelPortfoliosIPSAndAssetClasses/assetClassTargetAllocation.utils';
import { AssetClassesTargetAllocations } from '../addModelPortfoliosIPSAndAssetClasses/components/assetClassesTargetAllocations';
import type { ModelPortfolioGroupBy } from '../createModelPortfolio/modelPortfolioGroupBy';
import {
  DISCLAIMER_FIELD,
  type HouseholdIpsActuals,
  type HouseholdIpsFieldDefinition,
  type HouseholdIpsNarrativeKey,
  type HouseholdIpsOptions,
  type HouseholdIpsValues,
  IPS_NARRATIVE_FIELDS,
  isMissing,
} from './householdIpsFields';
import { HouseholdIpsGroupBySelect } from './householdIpsGroupBySelect';

export type HouseholdIpsEdits = {
  values: HouseholdIpsValues;
  /** True while something on screen would make the IPS invalid to submit. */
  blocked: boolean;
};

export type HouseholdIpsEditorArgs = {
  options: HouseholdIpsOptions;
  initialValues?: HouseholdIpsValues;
  actuals?: HouseholdIpsActuals;
  groupBy: ModelPortfolioGroupBy;
  onChange: (edits: HouseholdIpsEdits) => void;
  disabled?: boolean;
  /** Narrows the band rows for a column that is sharing the width. */
  compact?: boolean;
  /** Left off by the step that fills the width, which owns the bare ids. */
  testIdPrefix?: string;
};

/**
 * The IPS fields, editable, handed back one at a time so a step can stack them
 * in a column or lay each of them beside the same field of another model. The
 * step around them owns the buttons, so the edits and whether they can be
 * submitted are reported upwards on every change rather than collected when a
 * button is pressed.
 */
export const useHouseholdIpsEditor = ({
  options,
  initialValues,
  actuals,
  groupBy,
  onChange,
  disabled = false,
  compact = false,
  testIdPrefix,
}: HouseholdIpsEditorArgs) => {
  const { t } = useTranslation(['workflowCompletions', 'components']);
  const { translateBackendString, language } = useLocalization();
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);
  const [values, setValues] = useState<HouseholdIpsValues>({
    translatedName: initialValues?.translatedName ?? getLanguageDefaults(),
    translatedPortfolioDescription:
      initialValues?.translatedPortfolioDescription ?? getLanguageDefaults(),
    translatedInvestmentObjective:
      initialValues?.translatedInvestmentObjective ?? getLanguageDefaults(),
    translatedLiquidity:
      initialValues?.translatedLiquidity ?? getLanguageDefaults(),
    translatedPortfolioDisclaimer:
      initialValues?.translatedPortfolioDisclaimer ?? getLanguageDefaults(),
  });
  const [touched, setTouched] = useState<string[]>([]);
  const [allocations, setAllocations] = useState<AssetClassTargetAllocation[]>(
    toEditableAllocations(initialValues?.targetAllocations),
  );
  const allocationsEnabled = !!options?.enableAssetClassTargetAllocations;

  const testId = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

  const update = (
    valueKey: HouseholdIpsNarrativeKey,
    value: TranslatedString,
  ) => setValues((previous) => ({ ...previous, [valueKey]: value }));

  const markTouched = (valueKey: string) =>
    setTouched((previous) =>
      previous.includes(valueKey) ? previous : [...previous, valueKey],
    );

  const missingFields = IPS_NARRATIVE_FIELDS.filter(
    ({ optionKey, valueKey, richText }) =>
      isMissing(options?.[optionKey], values[valueKey], language, richText),
  );
  const blocked =
    missingFields.length > 0 ||
    (allocationsEnabled && hasUnusableAllocation(allocations));

  // Held by reference because the step around these fields rebuilds the
  // callback on every render, and reporting on that would never settle.
  const report = useRef(onChange);
  report.current = onChange;

  /**
   * Only what the advisor was actually shown.
   *
   * A field the admin turned off is still seeded from the model and never
   * touched, so handing it back would send the model's own value on as though
   * it had been typed.
   *
   * When the bands are switched off the step hands back none rather than an
   * empty list that would wipe the inherited ones.
   */
  useEffect(() => {
    const shown: HouseholdIpsValues = {};

    for (const { optionKey, valueKey } of IPS_NARRATIVE_FIELDS) {
      if (options?.[optionKey]?.enabled) shown[valueKey] = values[valueKey];
    }

    if (allocationsEnabled) {
      shown.targetAllocations = toStoredAllocations(allocations);
    }

    report.current({ values: shown, blocked });
  }, [options, values, allocations, allocationsEnabled, blocked]);

  /**
   * The rich text editor takes no `required` or `error` prop, so the marker and
   * the message are put together here instead. Without them a required
   * disclaimer holds the step back with nothing on screen to explain why.
   */
  const disclaimer = () => {
    const option = options?.[DISCLAIMER_FIELD.optionKey];
    const required = option?.required === FieldRequiredType.ERROR;
    const missing =
      touched.includes(DISCLAIMER_FIELD.valueKey) &&
      isMissing(
        option,
        values[DISCLAIMER_FIELD.valueKey],
        language,
        DISCLAIMER_FIELD.richText,
      );

    return (
      <>
        <TranslatableRichTextEditor
          testId={testId(DISCLAIMER_FIELD.testId)}
          label={`${translateBackendString(option?.label)}${required ? ' *' : ''}`}
          value={values[DISCLAIMER_FIELD.valueKey]}
          setValue={(value: TranslatedString) => {
            update(DISCLAIMER_FIELD.valueKey, value);
            markTouched(DISCLAIMER_FIELD.valueKey);
          }}
          fullToolbar={false}
        />
        {missing && (
          <Box data-testid={testId(`${DISCLAIMER_FIELD.testId}-error`)}>
            <HelpText
              text={t('components:translatableString.errorText')}
              tone='error'
            />
          </Box>
        )}
      </>
    );
  };

  return {
    /** Whether the bands are on screen at all. */
    hasBands: allocationsEnabled,

    /** Null for a field the admin turned off, so the step can skip its row. */
    field: ({
      optionKey,
      valueKey,
      testId: fieldTestId,
      richText,
    }: HouseholdIpsFieldDefinition): ReactNode => {
      if (!options?.[optionKey]?.enabled) return null;
      if (richText) return disclaimer();

      return (
        <TranslatableTextField
          testId={testId(fieldTestId)}
          label={translateBackendString(options?.[optionKey]?.label)}
          value={values[valueKey]}
          onChange={(value: TranslatedString) => {
            update(valueKey, value);
            markTouched(valueKey);
          }}
          onBlur={() => markTouched(valueKey)}
          disabled={disabled}
          fullWidth
          multiline
          rows={1}
          required={options?.[optionKey]?.required === FieldRequiredType.ERROR}
          error={
            touched.includes(valueKey) &&
            isMissing(options?.[optionKey], values[valueKey], language)
          }
        />
      );
    },

    bands: (): ReactNode => {
      if (!allocationsEnabled) return null;

      return (
        <>
          <Typography
            data-testid={testId('household-ips-target-allocations-label')}
            variant='labelSmall'
            component='div'
            sx={{ mb: '4px', color: tokens.labelColor }}
          >
            {translateBackendString(options?.assetClassTargetAllocationsLabel)}
          </Typography>
          <AssetClassesTargetAllocations
            testId={testId('household-ips-target-allocations')}
            targetAllocations={allocations}
            setTargetAllocations={setAllocations}
            securities={actuals?.securities}
            assetClassBranches={actuals?.assetClassBranches}
            strategy={actuals?.strategy}
            groupBy={groupBy}
            compact={compact}
            // The bands answer to this rather than to `disabled`. Left off,
            // they stay live through a save that has already taken its copy of
            // them, and a band moved in that moment is shown back to the
            // advisor as though it had been sent.
            readOnly={disabled}
          />
        </>
      );
    },
  };
};

/**
 * Whether the actual weights can be totalled at more than one asset class
 * level. Models built out of asset classes carry a single set of weights, so
 * there is nothing to choose between.
 */
export const hasGroupBy = (
  options: HouseholdIpsOptions,
  actuals?: HouseholdIpsActuals,
) =>
  !!options?.enableAssetClassTargetAllocations &&
  actuals?.strategy !== FinancialProductStrategy.AssetClass;

export type HouseholdIpsEditableColumnProps = HouseholdIpsEditorArgs & {
  onGroupByChange: (groupBy: ModelPortfolioGroupBy) => void;
};

/** The editable IPS fields stacked into one column. */
export const HouseholdIpsEditableColumn = ({
  onGroupByChange,
  ...args
}: HouseholdIpsEditableColumnProps) => {
  const { options, actuals, groupBy, disabled, testIdPrefix } = args;
  const editor = useHouseholdIpsEditor(args);
  const testId = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

  return (
    <Grid container spacing={2}>
      {IPS_NARRATIVE_FIELDS.map((definition) => {
        const field = editor.field(definition);

        return (
          field && (
            <Grid key={definition.valueKey} size={12}>
              {field}
            </Grid>
          )
        );
      })}

      {editor.hasBands && (
        <Grid size={12}>
          {hasGroupBy(options, actuals) && (
            <Box sx={{ mb: 2 }}>
              <HouseholdIpsGroupBySelect
                testId={testId('household-ips-group-by-select')}
                value={groupBy}
                onChange={onGroupByChange}
                disabled={disabled}
              />
            </Box>
          )}
          {editor.bands()}
        </Grid>
      )}
    </Grid>
  );
};

export default HouseholdIpsEditableColumn;
